import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { carsAPI, appointmentsAPI } from '../services/api';
import { CalendarIcon, Clock, CheckCircle2, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import Seo from '../components/Seo';

const timeSlots = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

const Booking = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const preSelectedCar = location.state?.car;

  const [cars, setCars] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    car_id: preSelectedCar?.id || '',
    date: undefined, time: '', message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    carsAPI.getAll({ status: 'available' })
      .then((res) => setCars(res.data || []))
      .catch((err) => console.error(err));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.car_id || !formData.date || !formData.time) {
      toast.error(t('booking.fillAll', 'Veuillez remplir tous les champs requis'));
      return;
    }
    setIsLoading(true);
    try {
      await appointmentsAPI.create({ ...formData, date: format(formData.date, 'yyyy-MM-dd') });
      setIsSubmitted(true);
      toast.success(t('booking.successToast', 'Demande envoyée avec succès !'));
      setTimeout(() => { setIsSubmitted(false); navigate('/'); }, 3500);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || t('booking.error', "Erreur lors de l'envoi. Veuillez réessayer."));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] pt-32 pb-20 flex items-center justify-center" data-testid="booking-success">
        <div className="text-center max-w-md mx-auto px-6 fade-up">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 mb-6">
            <CheckCircle2 size={30} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('booking.success', 'Demande envoyée')}
          </h1>
          <p className="text-slate-600">{t('booking.successMessage', 'Nous reviendrons vers vous sous 24h pour confirmer votre rendez-vous.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pt-24 lg:pt-28 pb-20" data-testid="booking-page">
      <Seo title={t('seo.booking.title')} description={t('seo.booking.desc')} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="section-eyebrow">{t('booking.title', 'Visite')}</span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900" data-testid="booking-heading" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('booking.appointment', 'Prendre rendez-vous')}
          </h1>
          <p className="mt-3 text-slate-600">{t('booking.subtitle', 'Visite uniquement sur rendez-vous. Nous revenons vers vous sous 24h.')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6" data-testid="booking-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="form-label">{t('booking.fullName', 'Nom complet')} *</label>
              <input
                name="name" value={formData.name} onChange={handleInputChange}
                placeholder="Jean Dupont"
                className="form-input" required
                data-testid="booking-name-input"
              />
            </div>
            <div>
              <label className="form-label">{t('booking.email', 'Email')} *</label>
              <input
                name="email" type="email" value={formData.email} onChange={handleInputChange}
                placeholder="jean@example.com"
                className="form-input" required
                data-testid="booking-email-input"
              />
            </div>
          </div>

          <div>
            <label className="form-label">{t('booking.phone', 'Téléphone')} *</label>
            <input
              name="phone" type="tel" value={formData.phone} onChange={handleInputChange}
              placeholder="+32 489 12 34 56"
              className="form-input" required
              data-testid="booking-phone-input"
            />
          </div>

          <div>
            <label className="form-label">{t('booking.vehicle', 'Véhicule')} *</label>
            <Select value={formData.car_id} onValueChange={(value) => setFormData((p) => ({ ...p, car_id: value }))}>
              <SelectTrigger className="form-input" data-testid="booking-vehicle-select">
                <SelectValue placeholder={t('booking.selectVehicle', 'Sélectionnez un véhicule')} />
              </SelectTrigger>
              <SelectContent>
                {cars.map((car) => (
                  <SelectItem key={car.id} value={car.id} className="text-sm">
                    {car.year} {car.make} {car.model} — €{(car.price || 0).toLocaleString('fr-BE')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="form-label">{t('booking.preferredDate', 'Date souhaitée')} *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="form-input text-left flex items-center justify-between"
                    data-testid="booking-date-picker"
                  >
                    <span className={formData.date ? 'text-slate-900' : 'text-slate-400'}>
                      {formData.date ? format(formData.date, 'PPP') : t('booking.pickDate', 'Choisir une date')}
                    </span>
                    <CalendarIcon className="h-4 w-4 text-blue-900" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-slate-200 rounded-xl">
                  <Calendar
                    mode="single" selected={formData.date}
                    onSelect={(date) => setFormData((p) => ({ ...p, date }))}
                    disabled={(date) => date < new Date() || date.getDay() === 0}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="form-label">{t('booking.preferredTime', 'Heure souhaitée')} *</label>
              <Select value={formData.time} onValueChange={(value) => setFormData((p) => ({ ...p, time: value }))}>
                <SelectTrigger className="form-input" data-testid="booking-time-select">
                  <SelectValue placeholder={t('booking.selectTime', 'Sélectionnez une heure')} />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot} value={slot}>
                      <span className="inline-flex items-center gap-2"><Clock size={14} className="text-blue-900" /> {slot}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="form-label">{t('booking.additionalInfo', 'Informations complémentaires')}</label>
            <textarea
              name="message" value={formData.message} onChange={handleInputChange}
              placeholder={t('booking.message', 'Précisions, questions…')}
              rows={4}
              className="form-input resize-none"
              data-testid="booking-message-input"
            />
          </div>

          <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
            <ShieldCheck size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              <span className="font-semibold">{t('booking.note', 'Note :')}</span> {t('booking.noteText', "Notre garage est uniquement accessible sur rendez-vous. Vous serez accueilli personnellement et sans pression commerciale.")}
            </p>
          </div>

          <button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="booking-submit-btn">
            {isLoading ? t('booking.submitting', 'Envoi…') : t('booking.submit', 'Envoyer ma demande')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Booking;
